import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { QualificationsAPI } from "../api/qualifications";
import type {
  Qualification,
  QualificationStats,
  SearchQualificationsResponse,
  SubmitQualificationRequest,
  UpdateQualificationRequest,
  AuditHistoryEntry,
} from "../types";
import { isDemoMode } from "@/features/utils/demo-mode";
import {
  mockQualificationsResult,
  mockQualificationStats,
  getMockQualification,
  getMockQualificationHistory,
} from "./use-qualifications.mock";

// Query keys
export const qualificationKeys = {
  all: ["qualifications"] as const,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  qualifications: (clientId: string, options?: any) =>
    [...qualificationKeys.all, "qualifications", clientId, options] as const,
  qualification: (clientId: string, qualificationId: string) =>
    [
      ...qualificationKeys.all,
      "qualification",
      clientId,
      qualificationId,
    ] as const,
  history: (clientId: string, qualificationId: string) =>
    [...qualificationKeys.all, "history", clientId, qualificationId] as const,
  stats: (clientId: string) =>
    [...qualificationKeys.all, "stats", clientId] as const,
};

/** How often a list or a submission in view looks for what a call just changed. */
const LIVE_REFRESH_MS = 10 * 1000;

// Query hooks
export function useQualifications(
  options?: {
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    customer_id?: string;
    form_id?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    score_min?: number;
    score_max?: number;
    completed?: boolean;
    reviewed?: boolean;
  },
  settings?: {
    /** Keep showing the previous page while a new filter or "load more" loads. */
    keepPrevious?: boolean;
  },
) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<SearchQualificationsResponse>({
    queryKey: qualificationKeys.qualifications(clientId, options),
    placeholderData: settings?.keepPrevious ? keepPreviousData : undefined,
    queryFn: () => {
      if (isDemoMode()) {
        // Apply filters to mock data
        let filtered = [...mockQualificationsResult.data];
        if (options?.customer_id) {
          filtered = filtered.filter(
            (q) => q.customer_id === options.customer_id,
          );
        }
        if (options?.form_id) {
          filtered = filtered.filter((q) => q.form_id === options.form_id);
        }
        if (options?.status) {
          filtered = filtered.filter((q) => q.status === options.status);
        }
        if (options?.limit) {
          filtered = filtered.slice(0, options.limit);
        }
        return Promise.resolve({
          ...mockQualificationsResult,
          data: filtered,
          total: filtered.length,
        });
      }
      return QualificationsAPI.searchQualifications(clientId, options);
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
    // A call's submission appears while it is on and is scored when it ends:
    // nothing pushes either, so the list looks again (tab visible only).
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useQualification(qualificationId: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<Qualification>({
    queryKey: qualificationKeys.qualification(clientId, qualificationId),
    queryFn: () => {
      if (isDemoMode()) {
        return Promise.resolve(getMockQualification(qualificationId));
      }
      return QualificationsAPI.getQualification(clientId, qualificationId);
    },
    enabled: !!clientId && !!qualificationId,
    staleTime: 30 * 1000,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useQualificationStats(options?: { enabled?: boolean }) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<QualificationStats>({
    queryKey: qualificationKeys.stats(clientId),
    queryFn: () => {
      if (isDemoMode()) {
        return Promise.resolve(mockQualificationStats);
      }
      return QualificationsAPI.getStats(clientId);
    },
    enabled: !!clientId && options?.enabled !== false,
    staleTime: 30 * 1000,
  });
}

export function useQualificationHistory(qualificationId: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<AuditHistoryEntry[]>({
    queryKey: qualificationKeys.history(clientId, qualificationId),
    queryFn: () => {
      if (isDemoMode()) {
        return Promise.resolve(getMockQualificationHistory(qualificationId));
      }
      return QualificationsAPI.getQualificationHistory(
        clientId,
        qualificationId,
      );
    },
    enabled: !!clientId && !!qualificationId,
    staleTime: 30 * 1000,
  });
}

// Mutation hooks
export function useSubmitQualification() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubmitQualificationRequest) =>
      QualificationsAPI.submitQualification(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationKeys.all });
    },
  });
}

export function useUpdateQualification() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      qualificationId,
      data,
    }: {
      qualificationId: string;
      data: UpdateQualificationRequest;
    }) =>
      QualificationsAPI.updateQualification(clientId, qualificationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationKeys.all });
    },
  });
}

export function useDeleteQualification() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (qualificationId: string) =>
      QualificationsAPI.deleteQualification(clientId, qualificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationKeys.all });
    },
  });
}

export function useApproveQualification() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (qualificationId: string) =>
      QualificationsAPI.approveQualification(clientId, qualificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationKeys.all });
    },
  });
}

export function useReopenQualification() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (qualificationId: string) =>
      QualificationsAPI.reopenQualification(clientId, qualificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationKeys.all });
    },
  });
}

export function useRejectQualification() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (qualificationId: string) =>
      QualificationsAPI.rejectQualification(clientId, qualificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationKeys.all });
    },
  });
}

export function useRecalculateQualification() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (qualificationId: string) =>
      QualificationsAPI.recalculateQualification(clientId, qualificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationKeys.all });
    },
  });
}
